// App.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams
} from 'react-router-dom';
import { isElectron, isCapacitor, isNative, isAndroid, getPostLoginDestination } from './utils/platform';
import { PushNotifications, Token, ActionPerformed } from '@capacitor/push-notifications';
import { Device } from '@capacitor/device';
import { App as CapacitorApp } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { SplashScreen } from '@capacitor/splash-screen';
//import { SafeArea } from '@capacitor-community/safe-area'; -> les 2 sont desinstallé
//import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support'; ->  les 2 sont desinstallé
// StatusBar removed - handled by @capawesome/capacitor-android-edge-to-edge-support plugin config

import { createLogger } from './services/Debug';
import { authManager } from './services/AuthManager';
import { iosLifecycleManager } from './services/IOSLifecycleManager';
import { secureTokenStorage } from './services/SecureTokenStorage';
// Auth Flow Components
import AuthFlow from './Subscription/AuthFlow';
import NoSubscription from './Subscription/NoSubscription';

// Existing Subscription Components
import SubscriptionPlans from './Subscription/SubscriptionPlans';
import SubscriptionSuccess from './Subscription/SubscriptionSuccess';
import SubscriptionCancel from './Subscription/SubscriptionCancel';
import SubscriptionManagement from './Subscription/SubscriptionManagement';

// Other Components
import Button from './components/buttons/Button';
import { userService, shortcutService, subscriptionService } from './services';
import { tokenManager } from './services/TokenManager';
import PasswordReset from './Subscription/PasswordReset';
import ReportList from './ReportList/ReportList';
import Recorder from './Dictaphone/Recorder.js';
import LiveRecorderPage from './Dictaphone/LiveRecorderPage.js';
import Settings from './Settings/Settings';
import Download from './Download/download';
import Uploader from './Electron/uploader.js';

import Privacy from './Privacy/Privacy';
import TermsOfService from './Guide/TermsOfService';
import FAQ from './Guide/FAQ';
import Guide from './Guide/Guide/Guide';
import Header from './components/Header/Header.js';
import Home from './Home/home.js';
import About from './About/About.js';
import Landing from './Landing/Landing';
import Contact from './Contact/Contact';
import './global.css';
import { LoadingProvider, useLoading } from './context/LoadingContext';
import { SocketProvider, useSocket } from './context/SocketContext';

import { AuthProvider, useAuth } from './context/AuthContext';
import { RecorderPanelProvider } from './context/RecorderPanelContext';
import SubscriptionRequired from './SubscriptionRequired/SubscriptionRequired';
import EmailVerificationOverlay from './components/overlays/EmailVerificationOverlay';

import MacroWindow from './Electron/windows/MacroWindow.js';
import MacroSearchWindow from './Electron/windows/MacroSearchWindow.js';
import HelperWindow from './Electron/windows/HelperWindow.js';
import BackgroundUploader from './Electron/BackgroundUploader/BackgroundUploader.js';
import AdminDashboard from './Admin/Admin';
import ReportTypePanel from './ReportTypes/ReportTypePanel';
import Templates from './Templates/Templates';
import EnterpriseAdmin from './EnterpriseAdmin/EnterpriseAdmin';
import PermissionModal from './components/PermissionModal/PermissionModal';
// Debug overlay removed. Use services/Debug.js createLogger/logWithSource for development logs.

// Global airplane mode key
const AIRPLANE_MODE_KEY = 'global_airplane_mode';
const PUSH_TOKEN_KEY = 'pending_push_token';
const PUSH_TOKEN_SENT_KEY = 'push_token_sent';
const BACKGROUND_TIME_KEY = 'app_background_time';
const MIN_SPLASH_DURATION = 2000; // Minimum splash screen duration in milliseconds
const BACKGROUND_THRESHOLD = 60000; // Show splash if app was in background for more than 1 minute

function App() {
  return (
    <LoadingProvider>
      <AuthProvider>
        <SocketProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <RecorderPanelProvider>
              <AppContent />
            </RecorderPanelProvider>
          </Router>
        </SocketProvider>
      </AuthProvider>
    </LoadingProvider>
  );
}

// Helper function to check if header should be suppressed
function shouldSuppressHeader(location, isAuthenticated, isSubscribed) {
  const authFlowRoutes = [
    '/auth',
    '/login',
    '/register',
    '/register-email',
    '/email-verification',
    '/code-verification',
    '/complete-profile',
    '/forgot-password',
    '/reset-password',
    '/subscription',
    '/subscription-plans',
    
    '/no-subscription',
    '/subscription-required'
  ];
  const isAuthFlowRoute = authFlowRoutes.some(route =>
    location.pathname === route || location.pathname.startsWith(route + '/')
  );

  return (
    (isElectron && (location.pathname === '/recorder' || location.pathname === '/macro-search' || location.pathname === '/live-recorder')) ||
    isAuthFlowRoute ||
    (isAuthenticated && !isSubscribed)
  );
}

// Recovery redirect component - handles /recover?token=xxx URLs
function RecoveryRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    // Get the token from the query string
    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    // Redirect to auth with the token
    if (token) {
      navigate(`/auth?token=${token}`, { replace: true });
    } else {
      // No token, just go to auth
      navigate('/auth', { replace: true });
    }
  }, [location.search, navigate]);

  return <div>Redirecting...</div>;
}

function ProtectedRoute({ children, isLoggingOut, requireSubscription = true }) {
  const { authReady, isAuthenticated, isSubscribed, loading } = useAuth();
  const { setLoading: setGlobalLoading } = useLoading();

  // Show loading indicator while auth is initializing
  React.useEffect(() => {
    if (!authReady && loading) {
      setGlobalLoading({
        isLoading: true,
        text: "Vérification de l'authentification...",
        showLogo: true
      });
    } else {
      setGlobalLoading(false);
    }
  }, [authReady, loading, setGlobalLoading]);

  if (!authReady) return null;

  // During logout, hold rendering to avoid redirecting before tokens are cleared
  if (isLoggingOut) return null;

  if (!isAuthenticated) {
    return <Navigate to="/auth" />;
  }

  if (requireSubscription && !isSubscribed) {
    return <Navigate to="/subscription-required" />;
  }

  // Onboarding is no longer required; do not block navigation based on onboarding status

  return children;
}

function AppContent() {
  const log = createLogger('AppContent');
  const { isAuthenticated, authReady, isSubscribed, profile } = useAuth();
  const [pushInitialized, setPushInitialized] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const { disconnect } = useSocket();
  // Subscription checks are centralized in AuthContext now
  const { setLoading: setGlobalLoading } = useLoading();
  const navigate = useNavigate();
  const location = useLocation();

  // Startup permissions check (Electron only)
  const [startupPermissions, setStartupPermissions] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionModalDismissed, setPermissionModalDismissed] = useState(false);

  // Check permissions on startup (Electron macOS only) - only when authenticated
  useEffect(() => {
    if (!isElectron || !isAuthenticated || !authReady || permissionModalDismissed) return;

    const checkPermissions = async () => {
      try {
        if (window.electronAPI?.checkAllPermissions) {
          const result = await window.electronAPI.checkAllPermissions();
          setStartupPermissions(result);
          if (result.platform === 'darwin' && !result.allGranted) {
            // Small delay before showing the modal (1.5s after login)
            setTimeout(() => {
              setShowPermissionModal(true);
            }, 1500);
          }
        }
      } catch (err) {
        log.error('Error checking startup permissions:', err);
      }
    };

    checkPermissions();
  }, [isAuthenticated, authReady, permissionModalDismissed]);

  const handleOpenPermissionSettings = async (type) => {
    if (!window.electronAPI) return;
    try {
      if (type === 'accessibility') {
        await window.electronAPI.openAccessibilitySettings();
      } else if (type === 'microphone') {
        await window.electronAPI.openMicrophoneSettings();
      } else if (type === 'screenCapture') {
        await window.electronAPI.openScreenCaptureSettings();
      }
    } catch (err) {
      log.error('Error opening settings:', err);
    }
  };

  const handleRestartApp = async () => {
    if (window.electronAPI?.restartApp) {
      await window.electronAPI.restartApp();
    }
  };

  const handleDismissPermissionModal = () => {
    setShowPermissionModal(false);
    setPermissionModalDismissed(true);
  };

  // Email verification overlay state
  const [showEmailVerificationOverlay, setShowEmailVerificationOverlay] = useState(false);
  const [emailVerificationDismissed, setEmailVerificationDismissed] = useState(false);
  const handledStripeSessionRef = React.useRef(null);

  // Check if email needs verification
  useEffect(() => {
    if (!authReady || !isAuthenticated || !profile || emailVerificationDismissed) {
      return;
    }

    // Helper function to check if createdAt is older than 24 hours
    const isOlderThan24Hours = (createdAt) => {
      if (!createdAt) return false;
      const created = new Date(createdAt);
      const now = new Date();
      const diffHours = (now - created) / (1000 * 60 * 60);
      return diffHours > 24;
    };

    // Show overlay if email is not verified and account is older than 24 hours
    if (profile.emailVerified === false && isOlderThan24Hours(profile.emailVerifiedAt || profile.createdAt)) {
      setShowEmailVerificationOverlay(true);
    }
  }, [authReady, isAuthenticated, profile, emailVerificationDismissed]);

  const handleDismissEmailVerification = () => {
    setShowEmailVerificationOverlay(false);
    setEmailVerificationDismissed(true);
  };

  // Handle Stripe success redirect when using HashRouter
  useEffect(() => {
    if (!authReady || !isAuthenticated) return;
    try {
      const url = new URL(window.location.href);
      const sid = url.searchParams.get('session_id');
      if (!sid || handledStripeSessionRef.current === sid) return;
      handledStripeSessionRef.current = sid;
      (async () => {
        try {
          await subscriptionService.verifySession(sid);
        } catch (_) {
          // Ignore UI error here; webhook will finalize subscription
        } finally {
          // Clean URL to avoid re-trigger
          url.searchParams.delete('session_id');
          const cleaned = `${url.origin}${url.pathname}${url.hash}`;
          window.history.replaceState({}, '', cleaned);
          // Navigate to success screen for confirmation UI
          if (location.pathname !== '/subscription/success') {
            navigate('/subscription/success', { replace: true });
          }
        }
      })();
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, isAuthenticated]);

  // Early registration listener to persist token ASAP (especially on iOS)
  useEffect(() => {
    if (!isCapacitor) return;

    const earlyRegistration = async (token) => {
      try {
        const value = token?.value;
        
        if (value) {
          await Preferences.set({ key: PUSH_TOKEN_KEY, value });
          await Preferences.set({ key: PUSH_TOKEN_SENT_KEY, value: '0' });
        }
      } catch (err) {
        log.error('[Push] Error storing early token:', err);
      }
    };

    PushNotifications.addListener('registration', earlyRegistration);
    return () => {
      // Keep other listeners intact; no global remove here
    };
  }, []);

  // Session expired modal state
const [showSessionExpired, setShowSessionExpired] = useState(false);
const [sessionExpiredReason, setSessionExpiredReason] = useState(null);
const [isRetryingLogin, setIsRetryingLogin] = useState(false);

  // Global API error alert state
  const [apiError, setApiError] = useState(null);

  // Ensure we never surface technical terms to users
  const sanitizeUserMessage = (raw) => {
    try {
      const message = String(raw || '');
      if (/\b(http|token)\b/i.test(message)) {
        return 'Une erreur est survenue. Veuillez réessayer.';
      }
      return message;
    } catch (e) {
      return 'Une erreur est survenue. Veuillez réessayer.';
    }
  };

  useEffect(() => {
    const onApiError = (event) => {
      const detail = event?.detail || {};
      setApiError({
        message: sanitizeUserMessage(detail.message || 'Une erreur est survenue.'),
        status: detail.status,
        code: detail.code,
        details: detail.details,
        timestamp: detail.timestamp
      });
    };
    window.addEventListener('api:error', onApiError);
    return () => window.removeEventListener('api:error', onApiError);
  }, []);

useEffect(() => {
  const onSessionExpired = (event) => {
    // Don't show if already logging out
    const authManager = require('./services/AuthManager').authManager;
    if (authManager.isLoggingOut) return;
    
    setSessionExpiredReason(event.detail || {});
    setShowSessionExpired(true);
  };
  window.addEventListener('session:expired', onSessionExpired);
  return () => window.removeEventListener('session:expired', onSessionExpired);
}, []);

const handleRetryLogin = async () => {
  setIsRetryingLogin(true);
  try {
    // Try to refresh token one more time
    await userService.refreshToken();
    
    // If successful, close modal and continue
    setShowSessionExpired(false);
    setSessionExpiredReason(null);
    
    // Refresh current page/data
    window.location.reload();
  } catch (error) {
    log.error('Retry login failed:', error);
    
    // If retry fails, show login screen
    setIsRetryingLogin(false);
    setShowSessionExpired(false);
    
    // Navigate to auth with a message
    navigate('/auth', { 
      state: { 
        message: 'Votre session a expiré. Veuillez vous reconnecter.',
        previousPath: location.pathname 
      }
    });
  }
};

  const enableGlobalAirplaneMode = useCallback(() => {
    localStorage.setItem(AIRPLANE_MODE_KEY, '1');
    setShowSessionExpired(false);
    navigate('/recorder');
  }, [navigate]);

  const disableGlobalAirplaneMode = useCallback(() => {
    localStorage.removeItem(AIRPLANE_MODE_KEY);
  }, []);

  useEffect(() => {
    // Avoid global loading overlay during the auth flow screens
    if (location.pathname.startsWith('/auth')) {
      return;
    }
    (async () => {
      setGlobalLoading({
        isLoading: true,
        text: "Initialisation de l'application...",
        showLogo: true
      });
      try {
        if (isCapacitor) {
          await iosLifecycleManager.initialize();
          await SplashScreen.hide();
        }
        // Apply Electron shortcuts once authenticated
        if (isAuthenticated && isElectron) {
          try {
            const platformShortcuts = await shortcutService.getPlatformShortcuts();
            const filtered = {};
            Object.entries(platformShortcuts || {}).forEach(([id, def]) => {
              if (!/^insertSection\d+$/.test(id) && id !== 'dictationServer' && def?.key) {
                filtered[id] = def;
              }
            });
            await window.electronAPI.setShortcuts(filtered);
          } catch (e) {
            log.warn('[App] Failed to apply backend shortcuts on init:', e);
          }
        }
      } catch (error) {
        log.error('App initialization error:', error);
      } finally {
        setGlobalLoading(false);
      }
    })();
  }, [setGlobalLoading, isAuthenticated]);

  // Handle iOS app lifecycle for token management and splash screen
  useEffect(() => {
    if (!isCapacitor) return;

    const handleAppStateChange = async ({ isActive }) => {
      
      if (isActive) {
        // App became active - check background time and show splash if needed
        try {
          // Check how long the app was in background
          const { value: backgroundTimeStr } = await Preferences.get({ key: BACKGROUND_TIME_KEY });
          const backgroundTime = backgroundTimeStr ? parseInt(backgroundTimeStr, 10) : null;
          
          if (backgroundTime) {
            const currentTime = Date.now();
            const timeDiff = currentTime - backgroundTime;
            
            // Show splash screen if app was in background for more than threshold
            if (timeDiff > BACKGROUND_THRESHOLD) {
              // Show splash screen
              await SplashScreen.show({
                autoHide: false,
                showDuration: 0
              });
              
              // Keep splash for minimum duration
              setTimeout(async () => {
                await SplashScreen.hide();
              }, MIN_SPLASH_DURATION);
            }
            
            // Clear the background time
            await Preferences.remove({ key: BACKGROUND_TIME_KEY });
          }
          
          // Validate tokens
          const token = tokenManager.getToken();
          if (token && tokenManager.isExpiringSoon()) {
            await userService.refreshToken();
          }

          // Clear delivered notifications to reset app icon badge
          try {
            await PushNotifications.removeAllDeliveredNotifications();
          } catch (err) {
            log.error('[Push] Error clearing delivered notifications on activate:', err);
          }
        } catch (error) {
          log.error('[App] Error handling app activation:', error);
        }
      } else {
        // App became inactive - persist critical data and timestamp
        try {
          // Store the time when app went to background
          await Preferences.set({ 
            key: BACKGROUND_TIME_KEY, 
            value: Date.now().toString() 
          });
          
          const token = tokenManager.getToken();
          if (token) {
            // Ensure tokens are persisted to secure storage
            const refreshToken = await tokenManager.getStoredRefreshToken();
            if (refreshToken) {
              await secureTokenStorage.storeSecureTokens(
                token,
                refreshToken,
                tokenManager.expiration
              );
            }
          }
        } catch (error) {
          log.error('[App] Error persisting data on app deactivation:', error);
        }
      }
    };

    // Listen for app state changes
    CapacitorApp.addListener('appStateChange', handleAppStateChange);

    return () => {
      CapacitorApp.removeAllListeners();
    };
  }, []);

  // On initial app open, clear any delivered notifications to reset badge
  useEffect(() => {
    if (!isCapacitor) return;
    (async () => {
      try {
        await PushNotifications.removeAllDeliveredNotifications();
      } catch (err) {
        log.error('[Push] Error clearing delivered notifications on app open:', err);
      }
    })();
  }, []);




  // Remove legacy manual auth check; AuthProvider handles it

  const initPush = async () => {
    if (!isCapacitor) {
      return;
    }
  
    try {
      // Get device info for platform detection
      const deviceInfo = await Device.getInfo();
      const platform = deviceInfo.platform; // 'ios' or 'android'
      
      
      
      // Check if we already have a stored token that wasn't sent
      const { value: storedToken } = await Preferences.get({ key: PUSH_TOKEN_KEY });
      const { value: sentFlag } = await Preferences.get({ key: PUSH_TOKEN_SENT_KEY });
      
      if (storedToken && sentFlag !== '1') {
        
        try {
          await userService.updateDeviceToken(storedToken, platform);
          await Preferences.set({ key: PUSH_TOKEN_SENT_KEY, value: '1' });

        } catch (error) {
          log.error('[Push] Failed to send stored token:', error);
        }
      }
      
      // Track if registration callback was received
      let registrationHandled = false;
      
      // Set up ALL listeners BEFORE checking permissions or registering
      
      // Registration success listener
      PushNotifications.addListener('registration', async (token) => {
        try {
          
          registrationHandled = true;
          
          // Store the token immediately
          await Preferences.set({ key: PUSH_TOKEN_KEY, value: token.value });
          
          // Try to send to backend
          try {
            await userService.updateDeviceToken(token.value, platform);
            await Preferences.set({ key: PUSH_TOKEN_SENT_KEY, value: '1' });
            
          } catch (error) {
            log.error('[Push] Failed to send token to backend:', error);
            // Token is stored, will retry on next app launch
            await Preferences.set({ key: PUSH_TOKEN_SENT_KEY, value: '0' });
          }
        } catch (error) {
          log.error('[Push] Error handling registration:', error);
        }
      });
  
      // Registration error listener
      PushNotifications.addListener('registrationError', (error) => {
        registrationHandled = true;
        log.error('[Push] Registration error:', error);
      });
  
      // Notification received listener (app in foreground)
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        
        
        // Check if this is a report completion notification
        if (notification.data?.type === 'report_completed' || 
            notification.title?.includes('rapport') || 
            notification.body?.includes('rapport')) {
          // Emit event for ReportList to refresh
          window.dispatchEvent(new CustomEvent('reportCompleted', { 
            detail: notification.data 
          }));
        }
      });
  
      // Notification action performed listener (notification tapped)
      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        
        
        // Check if this is a report completion notification
        if (action.notification.data?.type === 'report_completed' || 
            action.notification.title?.includes('rapport') || 
            action.notification.body?.includes('rapport')) {
          // Navigate to reports list
          navigate('/reports');
          
          // Emit event for ReportList to refresh
          window.dispatchEvent(new CustomEvent('reportCompleted', { 
            detail: action.notification.data 
          }));
        }
      });
  
      // NOW check permissions
      let permStatus = await PushNotifications.checkPermissions();
      
      
      if (permStatus.receive !== 'granted') {
        
        permStatus = await PushNotifications.requestPermissions();
      }
  
      if (permStatus.receive !== 'granted') {
        
        setPushInitialized(true); // Still mark as initialized even if permissions denied
        return;
      }
  
      
  
      // Register for notifications AFTER listeners are set up
      await PushNotifications.register();
      
  
      // iOS-specific fallback mechanism
      if (platform === 'ios') {
        // First timeout: retry registration if no callback received
        setTimeout(async () => {
          if (!registrationHandled) {
            log.warn('[Push] iOS registration callback not received after 5s, retrying...');
            
            try {
              // Try registering again
              await PushNotifications.register();
              
              
              // Second timeout: if still no callback, try to use stored token
              setTimeout(async () => {
                if (!registrationHandled) {
                  log.warn('[Push] Still no registration callback after retry');
                  
                  // Check if there's a stored token we can use
                  const { value: token } = await Preferences.get({ key: PUSH_TOKEN_KEY });
                  const { value: sent } = await Preferences.get({ key: PUSH_TOKEN_SENT_KEY });
                  
                  if (token && sent !== '1') {
                    
                    try {
                      await userService.updateDeviceToken(token, platform);
                      await Preferences.set({ key: PUSH_TOKEN_SENT_KEY, value: '1' });
                      
                    } catch (error) {
                      log.error('[Push] Failed to send stored token (fallback):', error);
                    }
                  } else if (!token) {
                    log.error('[Push] No token available after all attempts');
                  }
                }
              }, 3000); // Wait 3 more seconds after retry
            } catch (e) {
              log.error('[Push] Retry registration failed:', e);
            }
          }
        }, 5000); // Initial wait of 5 seconds
      }
  
      setPushInitialized(true);
      
      
    } catch (error) {
      log.error('[Push] Error initializing notifications:', error);
      setPushInitialized(true); // Mark as initialized even on error to prevent infinite retries
    }
  };

  // Initialize push notifications after authentication
  useEffect(() => {
    if (isAuthenticated && !pushInitialized && isCapacitor) {
      initPush();
    }
  }, [isAuthenticated, pushInitialized]);



// Edge-to-edge handled by @capawesome/capacitor-android-edge-to-edge-support plugin
// CSS uses var(--safe-area-inset-*, 0px) with 0px fallback


  const handleLoginSuccess = async () => {
    // Reset logout state
    setIsLoggingOut(false);
    // Notify listeners if needed
    authManager.isAuthenticated = true;
    authManager.notifyAuthStateChange(true);
    
    if (isCapacitor && !pushInitialized) {
      await initPush();
    }
    // Send any stored token post-login (especially for iOS early emission)
    if (isCapacitor) {
      try {
        const deviceInfo = await Device.getInfo();
        const platform = deviceInfo.platform || 'ios';
        const { value: storedToken } = await Preferences.get({ key: PUSH_TOKEN_KEY });
        const { value: sentFlag } = await Preferences.get({ key: PUSH_TOKEN_SENT_KEY });
        if (storedToken && sentFlag !== '1') {

          await userService.updateDeviceToken(storedToken, platform);
          await Preferences.set({ key: PUSH_TOKEN_SENT_KEY, value: '1' });
        }
      } catch (err) {
        log.error('[Push] Failed to send stored token after login:', err);
      }
    }
    
    if (isElectron) {
      const userId = localStorage.getItem('userId');
      if (userId) {
        window.electronAPI.storeChatId(userId);
        window.electronAPI.userAuthenticated();
      }
      // Fetch and apply Electron shortcuts from backend post-login
      try {
        const platformShortcuts = await shortcutService.getPlatformShortcuts();
        const filtered = {};
        Object.entries(platformShortcuts || {}).forEach(([id, def]) => {
          if (!/^insertSection\d+$/.test(id) && id !== 'dictationServer' && def?.key) {
            filtered[id] = def;
          }
        });
        await window.electronAPI.setShortcuts(filtered);
      } catch (e) {
        log.warn('[App] Failed to apply backend shortcuts after login:', e);
      }
    }
  };
  
  const handleLogout = async () => {
    // Set logout flag to prevent UI updates during logout
    setIsLoggingOut(true);
    
    // Clear session expired modal immediately
    setShowSessionExpired(false);
    
    disconnect();
    
    if (isCapacitor && pushInitialized) {
      try {
        await PushNotifications.unregister();
        await PushNotifications.removeAllListeners();
        setPushInitialized(false);
      } catch (error) {
        log.error('Error unregistering notifications:', error);
      }
    }
    
    // Use centralized logout (which includes navigation)
    await authManager.logout();
    
    // Electron: clear storage but do NOT quit the app
    if (isElectron) {
      try {
        if (window.electronAPI && window.electronAPI.clearAppStorage) {
          await window.electronAPI.clearAppStorage();
        }
      } catch (e) {
        // ignore
      }
    }

    // Allow UI to proceed with unauthenticated routing
    setIsLoggingOut(false);
  };
  
  
  if (!authReady) {
    return null; // LoadingProvider handles the loading display
  }

  const isAirplaneMode = localStorage.getItem(AIRPLANE_MODE_KEY) === '1';
  const suppressHeader = shouldSuppressHeader(location, isAuthenticated, isSubscribed);
  const shouldShowHeader = isAuthenticated && isSubscribed && !suppressHeader;

  // Session Expired Modal Component (rendered inside router to allow public route access)
  const SessionExpiredModal = () => {
    if (!showSessionExpired) return null;

    return (
      <div className="custom-modal-overlay">
        <div className="custom-modal">
          <h3>Session expirée</h3>

          <p style={{ color: '#666', marginBottom: '20px' }}>
            {sessionExpiredReason?.reason === 'no_refresh_token' ?
              "Nous n'avons pas pu vérifier votre session de connexion. Soit votre conenxion internet est perdue, soit vous avez été déconnecté, soit vous avez essayé de vous connecter avec un compte différent. Vous pouvez essayer de vous reconnecter automatiquement ou vous déconnecter pour vous reconnecter manuellement." :
              sessionExpiredReason?.reason === 'max_refresh_attempts' ?
              "Impossible de renouveler votre session après plusieurs tentatives." :
              isCapacitor ?
              "Votre session semble expirée. Vous pouvez essayer de vous reconnecter, continuer en mode avion (seul le dictaphone est accessible) ou vous déconnecter." :
              "Votre session semble expirée. Veuillez essayer de vous reconnecter."
            }
          </p>

          <div className="custom-modal-buttons" style={{ flexDirection: 'column', gap: '10px' }}>
            <Button
              variant="primary"
              onClick={handleRetryLogin}
              disabled={isRetryingLogin}
              style={{ width: '100%' }}
            >
              {isRetryingLogin ? 'Reconnexion...' : 'Réessayer la connexion'}
            </Button>

            {isCapacitor && (
              <Button
                variant="warning"
                onClick={enableGlobalAirplaneMode}
                disabled={isRetryingLogin}
                style={{ width: '100%' }}
              >
                Mode avion
              </Button>
            )}

            <Button
              variant="secondary"
              onClick={handleLogout}
              disabled={isRetryingLogin}
              style={{ width: '100%' }}
            >
              Se déconnecter
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`app ${isCapacitor ? (isAndroid ? 'capacitor-mode-android' : 'capacitor-mode-ios') : 'web-mode'} ${isElectron ? 'electron-mode' : ''} ${suppressHeader ? 'no-chrome' : ''}`}>
      {/* Startup Permission Modal (macOS) */}
      {showPermissionModal && startupPermissions && (
        <PermissionModal
          permissions={startupPermissions}
          onOpenSettings={handleOpenPermissionSettings}
          onRestartApp={handleRestartApp}
          onDismiss={handleDismissPermissionModal}
        />
      )}

      {shouldShowHeader && <Header handleLogout={handleLogout} />}

      {apiError && (
        <div className="custom-modal-overlay">
          <div className="custom-modal">
            <h3>Erreur</h3>
            <p style={{ color: '#666', marginBottom: '20px', whiteSpace: 'pre-wrap' }}>
              {apiError.message}
              
            </p>
            <div className="custom-modal-buttons">
              <Button 
                variant="primary" 
                onClick={() => setApiError(null)}
                style={{ minWidth: '120px' }}
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}

      <Routes>
        {/* Landing page - only for web, others go to auth flow */}
        <Route
          path="/"
          element={
            isAuthenticated ?
              <Navigate to={getPostLoginDestination()} /> :
              (isElectron || isCapacitor ? <Navigate to="/auth" /> : <Landing />)
          }
        />
        
        {/* New Unified Auth Flow */}
        <Route
          path="/auth"
          element={<AuthFlow />}
        />
        <Route
          path="/auth/*"
          element={<AuthFlow />}
        />
        
        {/* Legacy Auth routes - redirect to new auth flow */}
        <Route
          path="/login"
          element={<Navigate to="/auth" />}
        />
        <Route
          path="/register"
          element={<Navigate to="/auth" />}
        />
        <Route
          path="/register/verify"
          element={<Navigate to="/auth" />}
        />
        
        <Route
          path="/verify-new-email"
          element={<Navigate to="/auth" />}
        />
        <Route
          path="/register/complete"
          element={<Navigate to="/auth" />}
        />

        {/* Recovery route - redirects to auth with token */}
        <Route
          path="/recover"
          element={<RecoveryRedirect />}
        />

        {/* No Subscription Fallback */}
        <Route
          path="/no-subscription"
          element={<NoSubscription />}
        />

        {/* Subscription Required dedicated page */}
        <Route
          path="/subscription-required"
          element={
            <ProtectedRoute requireSubscription={false}>
              <SubscriptionRequired />
            </ProtectedRoute>
          }
        />

        
        {/* Subscription routes */}
        <Route
          path="/subscription/plans"
          element={
            <ProtectedRoute requireSubscription={false}>
              <SubscriptionPlans handleLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription/success"
          element={
            <ProtectedRoute requireSubscription={false}>
              <SubscriptionSuccess />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription/cancel"
          element={
            <ProtectedRoute requireSubscription={false}>
              <SubscriptionCancel />
            </ProtectedRoute>
          }
        />
 
        
        
        <Route
          path="/forgot-password"
          element={<Navigate to="/auth?step=forgot" />}
        />
        <Route
          path="/reset-password/:token"
          element={<PasswordReset />}
        />
        <Route
          path="/reset-password"
          element={<PasswordReset />}
        />
        
        {/* Protected routes with subscription check */}
        <Route
          path="/reports"
          element={
            <ProtectedRoute isLoggingOut={isLoggingOut}>
              <ReportList />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/recorder"
          element={
            <ProtectedRoute isLoggingOut={isLoggingOut}>
              <Recorder />
            </ProtectedRoute>
          }
        />
        {/*<Route
          path="/translator"
          element={
            <ProtectedRoute>
              <Translator />
            </ProtectedRoute>
          }
        />*/}
        <Route
          path="/settings"
          element={
            <ProtectedRoute isLoggingOut={isLoggingOut}>
               <Settings handleLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/subscription"
          element={
            <ProtectedRoute>
              <SubscriptionManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/enterprise"
          element={
            <ProtectedRoute>
              <EnterpriseAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/report-types"
          element={
            <ProtectedRoute>
              <ReportTypePanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates"
          element={
            <ProtectedRoute>
              <Templates />
            </ProtectedRoute>
          }
        />
      
        <Route
          path="/download"
          element={
            
              <Download />
            
          }
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute isLoggingOut={isLoggingOut}>
              <Home handleLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/uploader"
          element={
            <ProtectedRoute>
              {isNative ? <Uploader /> : <Navigate to="/reports" />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/about"
          element={
            <ProtectedRoute>
              <About />
            </ProtectedRoute>
          }
        />
        
        {/* Public routes */}
       
        <Route
          path="/privacy"
          element={<Privacy />}
        />
        <Route
          path="/terms"
          element={<TermsOfService />}
        />
        <Route
          path="/faq"
          element={<FAQ />}
        />
        <Route
          path="/guide"
          element={<Guide />}
        />
        <Route
          path="/about"
          element={<About />}
        />
        <Route
          path="/contact"
          element={<Contact />}
        />


        {/* Electron features accessible from Home */}
        <Route 
          path="/macros" 
          element={
            <ProtectedRoute>
              {isElectron ? <MacroWindow /> : <Navigate to="/home" />}
            </ProtectedRoute>
          }
        />
        <Route 
          path="/dictaphone" 
          element={
            <ProtectedRoute>
              {isElectron ? <Uploader /> : <Navigate to="/home" />}
            </ProtectedRoute>
          }
        />

        

        {/* Electron helper windows - redirect to reports if not electron */}
        <Route path="/macro-window" element={isElectron ? <MacroWindow /> : <Navigate to="/reports" />} />
        <Route path="/macro-search" element={<MacroSearchWindow />} />
        <Route path="/helper-window" element={isElectron ? <HelperWindow /> : <Navigate to="/reports" />} />
        <Route path="/background-uploader" element={isElectron ? <BackgroundUploader /> : <Navigate to="/reports" />} />
        <Route path="/live-recorder" element={
          isElectron ? <LiveRecorderPage /> : (
            <div className="web-live-recorder-page">
              <LiveRecorderPage />
            </div>
          )
        } />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>

      {/* Session expired modal - rendered inside router to allow public route access */}
      <SessionExpiredModal />
    </div>
  );
}

export default App;